
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast"
import { 
  Shield,
  LayoutDashboard, 
  Building2, 
  CreditCard, 
  Settings, 
  LogOut,
  Users,
  MessageSquare
} from "lucide-react";

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        toast.error("Erro ao fazer logout");
      } else {
        toast.success("Logout realizado com sucesso!");
        navigate("/admin/login");
      }
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Erro ao fazer logout");
    }
  };

  const menuItems = [
    { path: "/admin/control-center", label: "Central de Controle", icon: Shield },
    { path: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/admin/companies", label: "Empresas", icon: Building2 },
    { path: "/admin/users", label: "Usuários", icon: Users },
    { path: "/admin/plans", label: "Planos", icon: CreditCard },
    { path: "/admin/billing", label: "Cobranças", icon: CreditCard },
    { path: "/admin/communication", label: "Comunicação", icon: MessageSquare },
    { path: "/admin/whatsapp-chat", label: "Chat WhatsApp", icon: MessageSquare },
    { path: "/admin/settings", label: "Configurações", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-50/60">
      {/* Header */}
      <header className="bg-white border-b border-border/60 sticky top-0 z-20">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="w-full flex justify-between items-center h-16">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-xl bg-violet-600 flex items-center justify-center shrink-0">
                <Shield className="h-4 w-4 text-white" />
              </div>
              <h1 className="text-lg font-semibold text-foreground">
                Painel Administrativo
              </h1>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <LogOut size={16} />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <nav className="w-64 shrink-0 bg-white border-r border-border/60 min-h-[calc(100vh-64px)]">
          <div className="p-3">
            <div className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-violet-600 text-white shadow-[0_4px_10px_-2px_rgba(124,58,237,0.4)]"
                        : "text-muted-foreground hover:bg-violet-50 hover:text-violet-700"
                    }`}
                  >
                    <Icon size={16} />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>

        {/* Main content */}
        <main className="flex-1 p-4 sm:p-6">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
